// src/pages/Accounting/Reports/AccountList/components/ZoomDropdown.tsx
// Self-contained zoom + density control. Renders its own trigger button
// and absolute-positioned dropdown panel. Owns the open/close state.

import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check } from 'lucide-react';
import { DENSITIES, ZOOM_LEVELS } from '../constants';

interface ZoomDropdownProps {
  density: 'compact' | 'standard' | 'spacious';
  zoom: number;
  onDensityChange: (d: 'compact' | 'standard' | 'spacious') => void;
  onZoomChange: (z: number) => void;
}

export default function ZoomDropdown({ density, zoom, onDensityChange, onZoomChange }: ZoomDropdownProps) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [open]);

  const label = density === 'compact' ? 'Compact' : density === 'spacious' ? 'Spacious' : 'Standard';

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="inline-flex items-center gap-1 text-[12.5px] text-ink2 font-semibold cursor-pointer px-2 py-1.5 rounded-md border-none bg-transparent hover:bg-panel"
      >
        {label} | {zoom}%
        <ChevronDown size={11} strokeWidth={2.5} className="text-ink3" />
      </button>
      {open && (
        <div className="absolute left-0 top-full mt-1.5 w-[220px] bg-white border border-line rounded-xl shadow-[0_10px_32px_rgba(14,26,26,0.14)] py-1.5 z-50 max-h-[320px] overflow-y-auto">
          <div className="px-3.5 pt-2 pb-1 text-[10.5px] text-ink3 font-bold uppercase tracking-wider">
            Density
          </div>
          {DENSITIES.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => { onDensityChange(d.value); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3.5 py-2 text-left text-[12.5px] border-none bg-transparent cursor-pointer hover:bg-panel ${
                density === d.value ? 'text-teal-dark font-semibold' : 'text-ink'
              }`}
            >
              {d.label}
              {density === d.value && <Check size={13} strokeWidth={2.5} className="text-teal-dark" />}
            </button>
          ))}
          <div className="h-px bg-lineSoft my-1" />
          <div className="px-3.5 pt-2 pb-1 text-[10.5px] text-ink3 font-bold uppercase tracking-wider">
            Zoom
          </div>
          {ZOOM_LEVELS.map((z) => (
            <button
              key={z}
              type="button"
              onClick={() => { onZoomChange(z); setOpen(false); }}
              className={`w-full flex items-center justify-between px-3.5 py-2 text-left text-[12.5px] border-none bg-transparent cursor-pointer hover:bg-panel ${
                zoom === z ? 'text-teal-dark font-semibold' : 'text-ink'
              }`}
            >
              {z}%
              {zoom === z && <Check size={13} strokeWidth={2.5} className="text-teal-dark" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}